import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaBuilding, FaGlobe, FaEnvelope, FaPhoneAlt, FaMapMarkerAlt, FaLinkedin, FaTwitter, FaCloudUploadAlt, FaSave, FaCheckCircle, FaTrashAlt } from 'react-icons/fa';
import Loader from '../../shared/Loader';
import { showSuccess, showError, showLoading, closeLoading } from '../../../utils/Alert';
import { API_URL } from '../../../config';
import './CompanyBranding.css';

const CompanyBranding = () => {
    const [company, setCompany] = useState({
        name: '',
        industry: '',
        description: '',
        contactInfo: {
            email: '',
            phone: '',
            address: '',
            website: ''
        },
        socialLinks: {
            linkedin: '',
            twitter: ''
        },
        logoUrl: '',
        isVerified: false
    });
    const PREDEFINED_INDUSTRIES = [
        "Technology",
        "Finance",
        "Healthcare",
        "Education",
        "Marketing",
        "Manufacturing"
    ];
    const [isOther, setIsOther] = useState(false);
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });

    useEffect(() => {
        const fetchCompany = async () => {
            try {
                const token = localStorage.getItem('token');
                const res = await axios.get('/api/recruiter/company', {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (res.data) {
                    setCompany(res.data);
                    if (res.data.logoUrl) {
                        setLogoPreview(`${API_URL}${res.data.logoUrl}`);
                    }
                    if (res.data.industry && !PREDEFINED_INDUSTRIES.includes(res.data.industry)) {
                        setIsOther(true);
                    }
                }
            } catch (err) {
                console.error("No company found or error fetching:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchCompany();
    }, []);

    const handleIndustrySelect = (e) => {
        const value = e.target.value;
        if (value === "Other") {
            setIsOther(true);
            setCompany(prev => ({ ...prev, industry: '' }));
        } else {
            setIsOther(false);
            setCompany(prev => ({ ...prev, industry: value }));
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            setCompany(prev => ({
                ...prev,
                [parent]: { ...prev[parent], [child]: value }
            }));
        } else {
            setCompany(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleLogoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setLogoFile(file);
            setLogoPreview(URL.createObjectURL(file));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        showLoading('Saving Branding Profile...');

        try {
            const token = localStorage.getItem('token');
            const formData = new FormData();

            // Append basic fields
            formData.append('name', company.name);
            formData.append('industry', company.industry);
            formData.append('website', company.website);
            formData.append('description', company.description);
            formData.append('contactInfo', JSON.stringify(company.contactInfo));
            formData.append('socialLinks', JSON.stringify(company.socialLinks));

            if (logoFile) {
                formData.append('logo', logoFile);
            }

            const res = await axios.post('/api/recruiter/company', formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });

            closeLoading();
            showSuccess('Profile Updated', 'Company profile has been updated successfully!');
            setCompany(res.data.company);
        } catch (err) {
            closeLoading();
            showError('Update Failed', err.response?.data?.message || 'Failed to update company profile');
        } finally {
            setSaving(false);
        }
    };

    const handleVerifyPortal = async () => {
        if (company.isVerified) return;

        showLoading("Identity Verification", "Establishing secure connection with verification authority...");

        // Phase 2 of simulation
        setTimeout(() => {
            showLoading("Compliance Check", "Analyzing company documentation and industry standing...");
        }, 1500);

        // Actual API Call after simulation delay
        setTimeout(async () => {
            try {
                const token = localStorage.getItem('token');
                const res = await axios.patch('/api/recruiter/verify-company', {}, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (res.data.isVerified) {
                    setCompany(prev => ({ ...prev, isVerified: true }));
                    closeLoading();
                    showSuccess("Congratulations!", "Your company has been verified and awarded the Premium Employer Badge.");

                    // Sync with global user data if needed
                    window.dispatchEvent(new Event('profileUpdate'));
                }
            } catch (err) {
                closeLoading();
                showError("Verification Failed", err.response?.data?.message || "Could not complete verification at this time.");
            }
        }, 4000);
    };

    if (loading) return <Loader />;

    return (
        <div className="dashboard-overview fade-in">
            <div className="dashboard-header">
                <h1 className="page-title">Company Branding</h1>
                <p className="page-subtitle">Tell prospective candidates about your company's mission and culture.</p>
            </div>



            <form className="branding-form premium-card" onSubmit={handleSubmit}>
                <div className="form-sections-grid">
                    {/* Left Section: Identity & Logo */}
                    <div className="form-column main-identity">
                        <h3>Identity & Branding</h3>

                        <div className="logo-upload-section">
                            <div className="logo-preview-box">
                                {logoPreview ? (
                                    <img src={logoPreview} alt="Logo Preview" />
                                ) : (
                                    <FaBuilding size={40} color="#CBD5E1" />
                                )}
                            </div>
                            <div className="upload-btn-wrapper">
                                <label htmlFor="logo-upload" className="secondary-btn">
                                    <FaCloudUploadAlt /> {company.logoUrl ? 'Change Logo' : 'Upload Logo'}
                                </label>
                                <input
                                    id="logo-upload"
                                    type="file"
                                    accept="image/*"
                                    onChange={handleLogoChange}
                                    hidden
                                />
                                <p className="help-text">JPG, PNG or SVG. Max 2MB.</p>
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Company Name</label>
                            <div className="input-with-icon">
                                <FaBuilding className="input-icon" />
                                <input
                                    type="text"
                                    name="name"
                                    value={company.name}
                                    onChange={handleChange}
                                    placeholder="e.g. Acme Corporation"
                                    required
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Industry</label>
                            <select 
                                name="industry_select" 
                                value={isOther ? "Other" : company.industry} 
                                onChange={handleIndustrySelect} 
                                required
                            >
                                <option value="">Select Industry</option>
                                {PREDEFINED_INDUSTRIES.map(ind => (
                                    <option key={ind} value={ind}>{ind}</option>
                                ))}
                                <option value="Other">Other</option>
                            </select>
                        </div>

                        {isOther && (
                            <div className="form-group fade-in">
                                <label>Custom Industry Name</label>
                                <div className="input-with-icon">
                                    <FaBuilding className="input-icon" />
                                    <input
                                        type="text"
                                        name="industry"
                                        value={company.industry}
                                        onChange={handleChange}
                                        placeholder="Enter your industry"
                                        required
                                    />
                                </div>
                            </div>
                        )}

                        <div className="form-group">
                            <label>Website</label>
                            <div className="input-with-icon">
                                <FaGlobe className="input-icon" />
                                <input
                                    type="url"
                                    name="contactInfo.website"
                                    value={company.contactInfo.website}
                                    onChange={handleChange}
                                    placeholder="https://acme.com"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Middle Section: About & Contact */}
                    <div className="form-column contact-about">
                        <h3>About & Contact</h3>

                        <div className="form-group">
                            <label>Company Description</label>
                            <textarea
                                name="description"
                                value={company.description}
                                onChange={handleChange}
                                placeholder="Describe your company, values, and why people should work with you..."
                                rows="5"
                            ></textarea>
                        </div>

                        <div className="form-group">
                            <label>Official Email</label>
                            <div className="input-with-icon">
                                <FaEnvelope className="input-icon" />
                                <input
                                    type="email"
                                    name="contactInfo.email"
                                    value={company.contactInfo.email}
                                    onChange={handleChange}
                                    placeholder="hiring@acme.com"
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Phone Number</label>
                            <div className="input-with-icon">
                                <FaPhoneAlt className="input-icon" />
                                <input
                                    type="tel"
                                    name="contactInfo.phone"
                                    value={company.contactInfo.phone}
                                    onChange={handleChange}
                                    placeholder="+1 (555) 000-0000"
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Headquarters Address</label>
                            <div className="input-with-icon">
                                <FaMapMarkerAlt className="input-icon" />
                                <input
                                    type="text"
                                    name="contactInfo.address"
                                    value={company.contactInfo.address}
                                    onChange={handleChange}
                                    placeholder="123 Silicon Valley, CA"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Right Section: Social & Settings */}
                    <div className="form-column social-presence">
                        <h3>Social Presence</h3>

                        <div className="form-group">
                            <label>LinkedIn Profile</label>
                            <div className="input-with-icon">
                                <FaLinkedin className="input-icon" />
                                <input
                                    type="url"
                                    name="socialLinks.linkedin"
                                    value={company.socialLinks.linkedin}
                                    onChange={handleChange}
                                    placeholder="https://linkedin.com/company/acme"
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Twitter/X Profile</label>
                            <div className="input-with-icon">
                                <FaTwitter className="input-icon" />
                                <input
                                    type="url"
                                    name="socialLinks.twitter"
                                    value={company.socialLinks.twitter}
                                    onChange={handleChange}
                                    placeholder="https://twitter.com/acme"
                                />
                            </div>
                        </div>

                        <div className="branding-preview-card">
                            <p>Premium branding helps you attract <strong>3x more</strong> quality candidates.</p>
                            <div
                                className={`badge-preview ${company.isVerified ? 'verified-active' : ''}`}
                                onClick={handleVerifyPortal}
                                style={{ cursor: company.isVerified ? 'default' : 'pointer' }}
                            >
                                {company.isVerified ? (
                                    <>
                                        <FaCheckCircle className="verified-icon-btn" />
                                        <span>Verified Employer</span>
                                    </>
                                ) : (
                                    <span>Get Verified Employer Badge</span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="form-footer">
                    <button type="submit" className="primary-btn" disabled={saving}>
                        <FaSave /> {saving ? 'Saving Changes...' : 'Save Branding Profile'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default CompanyBranding;
